import { useState, useMemo, useEffect } from 'react';
import { StatusBadge } from '@/components/base/StatCard';
import { appointments, services, staffMembers, type Appointment, type AppointmentStatus, type ServiceItem } from '@/mocks/appointments';
import { financeTransactions, currencySymbols, type CurrencyCode } from '@/mocks/cash';
import { services as allServices } from '@/mocks/services';
import { customers as allCustomers } from '@/mocks/customers';
import CustomerCombobox from '@/components/base/CustomerCombobox';
import CalendarView, { type CalendarPeriod } from './components/CalendarView';
import GroupAppointmentModal from './components/GroupAppointmentModal';

const allStatuses: AppointmentStatus[] = ["Bekliyor", "Teyit Edildi", "Geldi", "Gelmedi", "İptal Etti"];

const statusQuickActions: { label: string; status: AppointmentStatus; color: string; icon: string }[] = [
  { label: 'Geldi', status: 'Geldi', color: '#10B981', icon: 'ri-check-line' },
  { label: 'Gelmedi', status: 'Gelmedi', color: '#EF4444', icon: 'ri-close-line' },
  { label: 'İptal', status: 'İptal Etti', color: '#9CA3AF', icon: 'ri-forbid-line' },
];

const quickFilters = [
  { label: 'Tümü', value: 'all' },
  { label: 'Bugün', value: 'today' },
  { label: 'Yarın', value: 'tomorrow' },
  { label: 'Ödeme Bekleyen', value: 'open', color: '#EF4444' },
  { label: 'Bu Hafta', value: 'week' },
];

/* ── Toast ── */
interface ToastItem { id: number; message: string; type: 'success' | 'error'; }

function useToast() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const show = (message: string, type: 'success' | 'error' = 'success') => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000);
  };
  const remove = (id: number) => setToasts(prev => prev.filter(t => t.id !== id));
  return { toasts, show, remove };
}

/* ── Multi-service Modal ── */
interface AppointmentModalProps {
  appointment: Appointment | null;
  initialDate?: string;
  initialTime?: string;
  initialStaff?: string;
  onClose: () => void;
  onSave: (appt: Appointment) => void;
}

function AppointmentModal({ appointment, initialDate, initialTime, initialStaff, onClose, onSave }: AppointmentModalProps) {
  const defaultDate = initialDate || new Date().toISOString().split('T')[0];
  const defaultTime = initialTime || '10:00';

  const [form, setForm] = useState<Partial<Appointment>>(appointment || {
    customerName: '',
    customerPhone: '',
    service: services[0],
    staff: initialStaff || staffMembers[0],
    date: defaultDate,
    time: defaultTime,
    duration: 60,
    source: 'Google',
    notes: '',
    status: 'Bekliyor',
    paymentStatus: 'Açık Ödeme',
    price: 0,
    whatsappConfirmed: false,
  });

  // Service items state
  const [serviceItems, setServiceItems] = useState<ServiceItem[]>(
    appointment?.serviceItems && appointment.serviceItems.length > 0
      ? appointment.serviceItems
      : appointment
        ? [{ serviceName: appointment.service, staff: appointment.staff, duration: appointment.duration, price: appointment.price }]
        : [{ serviceName: services[0], staff: initialStaff || staffMembers[0], duration: 60, price: 0 }]
  );

  useEffect(() => {
    if (!appointment) {
      const updates: Partial<typeof form> = {};
      if (initialDate) updates.date = initialDate;
      if (initialTime) updates.time = initialTime;
      if (initialStaff) updates.staff = initialStaff;
      if (Object.keys(updates).length > 0) {
        setForm(prev => ({ ...prev, ...updates }));
      }
    }
  }, [appointment, initialDate, initialTime, initialStaff]);

  // Sync serviceItems changes to form totals
  useEffect(() => {
    const totalDuration = serviceItems.reduce((sum, item) => sum + (item.duration || 0), 0);
    const totalPrice = serviceItems.reduce((sum, item) => sum + (item.price || 0), 0);
    const primary = serviceItems[0];
    setForm(prev => ({
      ...prev,
      duration: totalDuration,
      price: totalPrice,
      service: primary?.serviceName || services[0],
      staff: primary?.staff || staffMembers[0],
    }));
  }, [serviceItems]);

  const isEdit = !!appointment;

  const addServiceItem = () => {
    setServiceItems(prev => [
      ...prev,
      { serviceName: services[0], staff: staffMembers[0], duration: 60, price: 0 },
    ]);
  };

  const removeServiceItem = (index: number) => {
    if (serviceItems.length <= 1) return;
    setServiceItems(prev => prev.filter((_, i) => i !== index));
  };

  const updateServiceItem = (index: number, field: keyof ServiceItem, value: string | number) => {
    setServiceItems(prev => {
      const next = [...prev];
      const item = { ...next[index], [field]: value };

      // Auto-fill duration & price when service changes
      if (field === 'serviceName' && typeof value === 'string') {
        const matched = allServices.find(s => s.name === value);
        if (matched) {
          item.duration = matched.duration;
          item.price = matched.price;
        }
      }

      next[index] = item;
      return next;
    });
  };

  const totalDuration = serviceItems.reduce((sum, item) => sum + (item.duration || 0), 0);
  const totalPrice = serviceItems.reduce((sum, item) => sum + (item.price || 0), 0);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0" style={{ background: 'var(--modal-overlay)' }} onClick={onClose} />
      <div className="relative bg-theme-card rounded-spa w-full max-w-xl flex flex-col max-h-[90vh] animate-scale-in border overflow-hidden" style={{ borderColor: 'var(--card-border)' }}>
        <div className="sticky top-0 flex items-center justify-between p-4 border-b z-10 flex-shrink-0" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <h3 className="text-base font-semibold font-display" style={{ color: 'var(--text-primary)' }}>
            {isEdit ? 'Randevu Düzenle' : 'Yeni Randevu'}
          </h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto flex-1 min-h-0">
          <CustomerCombobox
            customers={allCustomers.map(c => ({ id: c.id, fullName: c.fullName, phone: c.phone, email: c.email, status: c.status }))}
            value={form.customerName ? { id: form.id || '', fullName: form.customerName || '', phone: form.customerPhone || '' } : null}
            onChange={(customer) => {
              if (customer) {
                setForm({ ...form, customerName: customer.fullName, customerPhone: customer.phone });
              }
            }}
            onCreateNew={(name, phone) => {
              setForm({ ...form, customerName: name, customerPhone: phone });
            }}
            label="Müşteri"
            required
          />

          {/* Service Items */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Hizmetler</label>
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{serviceItems.length} hizmet</span>
            </div>
            {serviceItems.map((item, index) => (
              <div key={index} className="p-3 rounded-spa border space-y-2.5" style={{ background: 'var(--bg-tertiary)', borderColor: 'var(--border-color)' }}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>Hizmet #{index + 1}</span>
                  {serviceItems.length > 1 && (
                    <button
                      onClick={() => removeServiceItem(index)}
                      className="w-6 h-6 flex items-center justify-center rounded-full cursor-pointer transition-colors hover:bg-red-50"
                      title="Kaldır"
                    >
                      <i className="ri-close-line text-xs" style={{ color: '#EF4444' }} />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  <div>
                    <label className="text-[10px] font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Hizmet</label>
                    <select
                      className="spa-input text-sm py-2"
                      value={item.serviceName}
                      onChange={e => updateServiceItem(index, 'serviceName', e.target.value)}
                    >
                      {services.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Personel</label>
                    <select
                      className="spa-input text-sm py-2"
                      value={item.staff}
                      onChange={e => updateServiceItem(index, 'staff', e.target.value)}
                    >
                      {staffMembers.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  <div>
                    <label className="text-[10px] font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Süre (dk)</label>
                    <input
                      type="number"
                      className="spa-input text-sm py-2"
                      value={item.duration}
                      onChange={e => updateServiceItem(index, 'duration', Number(e.target.value))}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Fiyat (₺)</label>
                    <input
                      type="number"
                      className="spa-input text-sm py-2"
                      value={item.price}
                      onChange={e => updateServiceItem(index, 'price', Number(e.target.value))}
                    />
                  </div>
                </div>
              </div>
            ))}
            <button
              onClick={addServiceItem}
              className="w-full py-2 rounded-spa text-xs font-medium cursor-pointer transition-all border border-dashed flex items-center justify-center gap-1.5"
              style={{ borderColor: 'var(--gold)', color: 'var(--gold)', background: 'rgba(201,169,98,0.06)' }}
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-add-line" /></div>
              Hizmet Ekle
            </button>
          </div>

          {/* Totals */}
          <div className="flex items-center justify-between p-3 rounded-spa border" style={{ background: 'var(--bg-tertiary)', borderColor: 'var(--border-color)' }}>
            <div className="flex items-center gap-4">
              <div>
                <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Toplam Süre</p>
                <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{totalDuration} dk</p>
              </div>
              <div className="w-px h-6" style={{ background: 'var(--border-color)' }} />
              <div>
                <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Toplam Tutar</p>
                <p className="text-sm font-semibold" style={{ color: 'var(--gold)' }}>₺{totalPrice.toLocaleString('tr-TR')}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Tarih</label>
              <input type="date" className="spa-input" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Saat</label>
              <input type="time" className="spa-input" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Süre (dk)</label>
              <input type="number" className="spa-input" value={form.duration || totalDuration} readOnly style={{ background: 'var(--bg-tertiary)', color: 'var(--text-muted)' }} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Kaynak</label>
              <select className="spa-input" value={form.source} onChange={e => setForm({ ...form, source: e.target.value as Appointment['source'] })}>
                {['Google', 'Meta', 'WhatsApp', 'Tavsiye', 'Tekrar Müşteri', 'Çağrı'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Tutar (₺)</label>
              <input type="number" className="spa-input" value={form.price || totalPrice} readOnly style={{ background: 'var(--bg-tertiary)', color: 'var(--text-muted)' }} />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Not</label>
            <textarea
              className="spa-input min-h-[80px] resize-none"
              maxLength={500}
              value={form.notes || ''}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              placeholder="Randevu notu..."
            />
            <p className="text-[10px] mt-1 text-right" style={{ color: 'var(--text-muted)' }}>{(form.notes || '').length}/500</p>
          </div>
        </div>

        <div className="sticky bottom-0 flex items-center justify-end gap-2 p-4 border-t" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <button onClick={onClose} className="spa-btn-secondary text-sm">İptal</button>
          <button
            onClick={() => {
              if (!form.customerName?.trim()) return;
              const saved: Appointment = {
                ...(form as Appointment),
                id: form.id || `ap-${Date.now()}`,
                createdAt: form.createdAt || new Date().toISOString().split('T')[0],
                customerPhone: form.customerPhone || '',
                duration: totalDuration,
                price: totalPrice,
                serviceItems,
              };
              onSave(saved);
              onClose();
            }}
            className="spa-btn-primary text-sm"
          >
            {isEdit ? 'Güncelle' : 'Oluştur'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Helper: Date navigation ── */
function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

function addMonths(dateStr: string, months: number): string {
  const d = new Date(dateStr + 'T00:00:00');
  d.setMonth(d.getMonth() + months);
  return d.toISOString().split('T')[0];
}

/* ── Page ── */
export default function RandevularPage() {
  const [viewMode, setViewMode] = useState<'list' | 'card' | 'calendar'>('list');
  const [calendarPeriod, setCalendarPeriod] = useState<CalendarPeriod>('daily');
  const [calendarDate, setCalendarDate] = useState<string>('2026-05-08');

  const [quickFilter, setQuickFilter] = useState('today');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [staffFilter, setStaffFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [apptList, setApptList] = useState<Appointment[]>(appointments);

  const [modalOpen, setModalOpen] = useState(false);
  const [editAppt, setEditAppt] = useState<Appointment | null>(null);
  const [detailAppt, setDetailAppt] = useState<Appointment | null>(null);
  const [modalInitialDate, setModalInitialDate] = useState<string | undefined>();
  const [modalInitialTime, setModalInitialTime] = useState<string | undefined>();
  const [modalInitialStaff, setModalInitialStaff] = useState<string | undefined>();
  const [groupModalOpen, setGroupModalOpen] = useState(false);

  const { toasts, show, remove } = useToast();

  /* ── Payment Modal ── */
  const [paymentAppt, setPaymentAppt] = useState<Appointment | null>(null);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('Nakit');
  const [payNotes, setPayNotes] = useState('');
  const [payCurrency, setPayCurrency] = useState<CurrencyCode>('TRY');
  const [payServiceAmount, setPayServiceAmount] = useState('');
  const [payServiceCurrency, setPayServiceCurrency] = useState<CurrencyCode>('TRY');
  const [paySplitMode, setPaySplitMode] = useState(false);
  const [paySplits, setPaySplits] = useState<Array<{amount: number; currency: CurrencyCode; method: string; bankName?: string; notes?: string}>>([{amount: 0, currency: 'TRY', method: 'Nakit', notes: ''}]);
  const payMethods = ['Nakit','Kredi Kartı','Havale','Açık Ödeme','Eksik Ödeme'];
  const bankListPay = ['Ziraat Bankası','Garanti Bankası','İş Bankası','Akbank','Yapı Kredi','Denizbank','Halkbank','Vakifbank'];
  const bankList = ['Ziraat Bankası','Garanti Bankası','İş Bankası','Akbank','Yapı Kredi','Denizbank','Halkbank','Vakıfbank'];

  const handlePaySave = () => {
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const timeStr = now.toTimeString().slice(0, 5);
    const serviceNum = payServiceAmount ? Number(payServiceAmount) : Number(payAmount);
    const serviceCur = payServiceCurrency || payCurrency;

    if (paySplitMode) {
      const validSplits = paySplits.filter(s => s.amount > 0);
      if (validSplits.length === 0) { show('Geçerli tutar girin.', 'error'); return; }
      const groupId = `split-${Date.now()}`;
      validSplits.forEach((sp, i) => {
        const isCash = sp.method === 'Nakit';
        financeTransactions.unshift({
          id: `fin-${Date.now()}-${i}`,
          date: dateStr,
          time: timeStr,
          customerName: paymentAppt!.customerName,
          description: paymentAppt!.service,
          amount: sp.amount,
          currency: sp.currency || 'TRY',
          serviceAmount: i === 0 ? serviceNum : undefined,
          serviceCurrency: i === 0 ? serviceCur : undefined,
          type: 'income' as const,
          account: isCash ? 'cash' : 'bank',
          bankName: isCash ? undefined : (sp.bankName || undefined),
          paymentType: sp.method as any,
          notes: sp.notes || payNotes.trim(),
          splitGroupId: groupId,
        });
      });
      const total = validSplits.reduce((s, sp) => s + sp.amount, 0);
      show(`${total.toLocaleString('tr-TR')} TL parçalı tahsilat kaydedildi.`, 'success');
    } else {
      const num = Number(payAmount);
      if (!num || num <= 0) { show('Geçerli tutar girin.', 'error'); return; }
      financeTransactions.unshift({
        id: `fin-${Date.now()}`,
        date: dateStr,
        time: timeStr,
        customerName: paymentAppt!.customerName,
        description: paymentAppt!.service,
        amount: num,
        currency: payCurrency,
        serviceAmount: serviceNum,
        serviceCurrency: serviceCur,
        type: 'income' as const,
        account: payMethod === 'Nakit' ? 'cash' : 'bank',
        paymentType: payMethod as any,
        notes: payNotes.trim(),
      });
      show(`${currencySymbols[payCurrency]}${num.toLocaleString('tr-TR')} tahsilat kaydedildi.`, 'success');
    }

    // Randevu ödeme durumunu güncelle
    setApptList(prev => prev.map(a => a.id === paymentAppt!.id ? { ...a, paymentStatus: 'Ödendi' as const } : a));
    if (detailAppt?.id === paymentAppt!.id) {
      setDetailAppt(prev => prev ? { ...prev, paymentStatus: 'Ödendi' } : prev);
    }

    setPaymentAppt(null);
    setPayAmount('');
    setPayMethod('Nakit');
    setPayNotes('');
    setPayCurrency('TRY');
    setPayServiceAmount('');
    setPayServiceCurrency('TRY');
    setPaySplitMode(false);
    setPaySplits([{amount: 0, currency: 'TRY', method: 'Nakit', notes: ''}]);
  };

  const openPayment = (appt: Appointment) => {
    setPaymentAppt(appt);
    setPayAmount(appt.price.toString());
    setPayServiceAmount(appt.price.toString());
    setPayServiceCurrency('TRY');
    setPayMethod('Nakit');
    setPayNotes('');
    setPayCurrency('TRY');
    setPaySplitMode(false);
    setPaySplits([{amount: 0, currency: 'TRY', method: 'Nakit', notes: ''}]);
  };

  const todayStr = '2026-05-08';
  const yesterdayStr = '2026-05-07';
  const tomorrowStr = '2026-05-09';

  const openTodayCount = useMemo(() => {
    return apptList.filter(a => a.date === todayStr && a.paymentStatus !== 'Ödendi').length;
  }, [apptList]);

  /* Filtered for list/card views */
  const filtered = useMemo(() => {
    let data = [...apptList];
    if (quickFilter === 'open') data = data.filter(a => a.paymentStatus !== 'Ödendi');
    if (quickFilter === 'today') data = data.filter(a => a.date === todayStr);
    if (quickFilter === 'yesterday') data = data.filter(a => a.date === yesterdayStr);
    if (quickFilter === 'tomorrow') data = data.filter(a => a.date === tomorrowStr);
    if (quickFilter === 'week') {
      const start = new Date(todayStr + 'T00:00:00');
      const day = start.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      start.setDate(start.getDate() + diff);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      data = data.filter(a => {
        const ad = new Date(a.date + 'T00:00:00');
        return ad >= start && ad <= end;
      });
    }
    if (statusFilter !== 'all') data = data.filter(a => a.status === statusFilter);
    if (staffFilter !== 'all') data = data.filter(a =>
      a.staff === staffFilter || a.serviceItems?.some(si => si.staff === staffFilter)
    );
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      data = data.filter(a =>
        a.customerName.toLowerCase().includes(q) ||
        a.service.toLowerCase().includes(q) ||
        (a.customerPhone || '').toLowerCase().includes(q) ||
        a.serviceItems?.some(si => si.serviceName.toLowerCase().includes(q))
      );
    }
    return data.sort((a, b) => `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`));
  }, [apptList, quickFilter, statusFilter, staffFilter, searchQuery]);

  /* Calendar appointments */
  const calendarAppointments = useMemo(() => {
    let data = [...apptList];
    if (statusFilter !== 'all') data = data.filter(a => a.status === statusFilter);
    if (staffFilter !== 'all') data = data.filter(a =>
      a.staff === staffFilter || a.serviceItems?.some(si => si.staff === staffFilter)
    );
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      data = data.filter(a =>
        a.customerName.toLowerCase().includes(q) ||
        a.service.toLowerCase().includes(q) ||
        (a.customerPhone || '').toLowerCase().includes(q)
      );
    }
    return data;
  }, [apptList, statusFilter, staffFilter, searchQuery]);

  const handleStatusChange = (id: string, newStatus: AppointmentStatus) => {
    setApptList(prev => prev.map(a => a.id === id ? { ...a, status: newStatus } : a));
  };

  const handleGroupSave = (appts: Appointment[]) => {
    setApptList(prev => [...appts, ...prev]);
    show(`${appts.length} randevu başarıyla oluşturuldu`, 'success');
  };

  const handleSave = (appt: Appointment) => {
    if (editAppt) {
      setApptList(prev => prev.map(a => a.id === appt.id ? appt : a));
      show('Randevu güncellendi', 'success');
    } else {
      setApptList(prev => [appt, ...prev]);
      show('Yeni randevu başarıyla eklendi', 'success');
    }
    setEditAppt(null);
    setModalInitialDate(undefined);
    setModalInitialTime(undefined);
    setModalInitialStaff(undefined);
  };

  const openCreate = (date?: string, time?: string, staff?: string) => {
    setEditAppt(null);
    setModalInitialDate(date);
    setModalInitialTime(time);
    setModalInitialStaff(staff);
    setModalOpen(true);
  };

  const openEdit = (appt: Appointment) => {
    setEditAppt(appt);
    setModalInitialDate(undefined);
    setModalInitialTime(undefined);
    setModalInitialStaff(undefined);
    setModalOpen(true);
  };

  const handleCalendarSlot = (date: string, time?: string, staff?: string) => {
    openCreate(date, time || '10:00', staff);
  };

  const navigatePrev = () => {
    if (calendarPeriod === 'daily') setCalendarDate(d => addDays(d, -1));
    else if (calendarPeriod === 'weekly') setCalendarDate(d => addDays(d, -7));
    else setCalendarDate(d => addMonths(d, -1));
  };

  const navigateNext = () => {
    if (calendarPeriod === 'daily') setCalendarDate(d => addDays(d, 1));
    else if (calendarPeriod === 'weekly') setCalendarDate(d => addDays(d, 7));
    else setCalendarDate(d => addMonths(d, 1));
  };

  const navigateToday = () => {
    setCalendarDate(todayStr);
  };

  const staffCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    staffMembers.forEach(s => counts[s] = 0);
    filtered.forEach(a => {
      a.serviceItems?.forEach(si => {
        if (counts[si.staff] !== undefined) counts[si.staff]++;
      });
      if (!a.serviceItems || a.serviceItems.length === 0) {
        if (counts[a.staff] !== undefined) counts[a.staff]++;
      }
    });
    return counts;
  }, [filtered]);

  const calendarNavLabel = useMemo(() => {
    const d = new Date(calendarDate + 'T00:00:00');
    if (calendarPeriod === 'daily') {
      return d.toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    }
    if (calendarPeriod === 'weekly') {
      const start = new Date(calendarDate + 'T00:00:00');
      const day = start.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      start.setDate(start.getDate() + diff);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      const fmt = (date: Date) => date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
      return `${fmt(start)} - ${fmt(end)}`;
    }
    return d.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
  }, [calendarDate, calendarPeriod]);

  return (
    <div className="space-y-4 animate-fade-in relative">
      {/* Toasts */}
      <div className="fixed top-4 right-4 z-[200] space-y-2">
        {toasts.map(t => (
          <div
            key={t.id}
            onClick={() => remove(t.id)}
            className="px-4 py-3 rounded-spa text-sm font-medium cursor-pointer animate-fade-in flex items-center gap-2 border"
            style={{
              background: t.type === 'success' ? '#10B981' : '#EF4444',
              color: '#FFFFFF',
              borderColor: t.type === 'success' ? '#059669' : '#DC2626',
            }}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={t.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} />
            </div>
            {t.message}
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold font-display" style={{ color: 'var(--text-primary)' }}>
            Randevular
          </h1>
          <p className="text-sm mt-1 flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>
            {viewMode === 'calendar' ? calendarNavLabel : `${filtered.length} randevu gösteriliyor`}
            {openTodayCount > 0 && (
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: '#EF4444', color: '#FFFFFF' }}>
                {openTodayCount} açık hesap
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setGroupModalOpen(true)}
            className="spa-btn-secondary text-sm flex items-center gap-2"
          >
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-team-line text-sm" /></div>
            Grup Randevusu
          </button>
          <button onClick={() => openCreate()} className="spa-btn-primary text-sm flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></div>
            Yeni Randevu
          </button>
        </div>
      </div>

      {/* View Mode Switch */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-spa-sm overflow-hidden border" style={{ borderColor: 'var(--border-color)' }}>
          {[
            { key: 'list' as const, icon: 'ri-list-check-2', label: 'Liste' },
            { key: 'card' as const, icon: 'ri-grid-line', label: 'Kart' },
            { key: 'calendar' as const, icon: 'ri-calendar-line', label: 'Takvim' },
          ].map(v => (
            <button
              key={v.key}
              onClick={() => setViewMode(v.key)}
              className="px-3 py-2 text-sm cursor-pointer flex items-center gap-1.5 transition-all whitespace-nowrap"
              style={{
                background: viewMode === v.key ? 'var(--gold)' : 'var(--bg-tertiary)',
                color: viewMode === v.key ? '#FFFFFF' : 'var(--text-secondary)',
              }}
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className={v.icon} /></div>
              {v.label}
            </button>
          ))}
        </div>

        {viewMode === 'calendar' && (
          <div className="flex rounded-spa-sm overflow-hidden border" style={{ borderColor: 'var(--border-color)' }}>
            {([
              { key: 'daily' as CalendarPeriod, label: 'Günlük' },
              { key: 'weekly' as CalendarPeriod, label: 'Haftalık' },
              { key: 'monthly' as CalendarPeriod, label: 'Aylık' },
            ]).map(p => (
              <button
                key={p.key}
                onClick={() => setCalendarPeriod(p.key)}
                className="px-3 py-2 text-sm cursor-pointer transition-all whitespace-nowrap"
                style={{
                  background: calendarPeriod === p.key ? 'linear-gradient(135deg, var(--gold), var(--gold-dark))' : 'var(--bg-tertiary)',
                  color: calendarPeriod === p.key ? '#FFFFFF' : 'var(--text-secondary)',
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Quick Filters */}
      {viewMode !== 'calendar' && (
        <div className="flex flex-wrap gap-2">
          {quickFilters.map(f => (
            <button
              key={f.value}
              onClick={() => setQuickFilter(f.value)}
              className="px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
              style={quickFilter === f.value
                ? (f.value === 'open'
                    ? { background: 'linear-gradient(135deg, #EF4444, #DC2626)', color: '#FFFFFF' }
                    : { background: 'linear-gradient(135deg, var(--gold), var(--gold-dark))', color: '#FFFFFF' }
                  )
                : { background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }
              }
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {/* Calendar Navigation */}
      {viewMode === 'calendar' && (
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={navigatePrev} className="w-8 h-8 rounded-spa flex items-center justify-center cursor-pointer transition-colors hover:bg-theme-hover border" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-s-line" /></div>
          </button>
          <button onClick={navigateToday} className="px-3 py-1.5 rounded-spa text-xs font-medium cursor-pointer transition-colors hover:bg-theme-hover border whitespace-nowrap" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
            Bugün
          </button>
          <button onClick={navigateNext} className="w-8 h-8 rounded-spa flex items-center justify-center cursor-pointer transition-colors hover:bg-theme-hover border" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line" /></div>
          </button>
          <span className="text-sm font-medium font-display ml-1" style={{ color: 'var(--text-primary)' }}>
            {calendarNavLabel}
          </span>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
            <i className="ri-search-line text-sm" style={{ color: 'var(--text-muted)' }} />
          </div>
          <input
            className="spa-input pl-10"
            placeholder="Müşteri, hizmet veya telefon ara..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <select
            className="spa-input text-sm min-w-[120px]"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="all">Tüm Durumlar</option>
            {allStatuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            className="spa-input text-sm min-w-[120px]"
            value={staffFilter}
            onChange={e => setStaffFilter(e.target.value)}
          >
            <option value="all">Tüm Personel</option>
            {staffMembers.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* Staff filter chips */}
      <div className="flex flex-wrap gap-2">
        {staffMembers.map(staff => (
          <button
            key={staff}
            onClick={() => setStaffFilter(staffFilter === staff ? 'all' : staff)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap border ${
              staffFilter === staff ? 'border-[var(--gold)]' : 'border-transparent'
            }`}
            style={{
              background: staffFilter === staff ? 'rgba(201,169,98,0.12)' : 'var(--bg-tertiary)',
              color: staffFilter === staff ? 'var(--gold)' : 'var(--text-secondary)',
            }}
          >
            {staff} <span className="ml-1 opacity-60">({staffCounts[staff] || 0})</span>
          </button>
        ))}
      </div>

      {/* ── CALENDAR VIEW ── */}
      {viewMode === 'calendar' && (
        <CalendarView
          period={calendarPeriod}
          currentDate={calendarDate}
          appointments={calendarAppointments}
          onSlotClick={handleCalendarSlot}
          onAppointmentClick={setDetailAppt}
          onAppointmentUpdate={(appt) => {
            setApptList(prev => prev.map(a => a.id === appt.id ? appt : a));
          }}
        />
      )}

      {/* ── LIST VIEW ── */}
      {viewMode === 'list' && (
        <div className="spa-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                  {['Müşteri', 'Hizmetler', 'Personel', 'Tarih/Saat', 'Kaynak', 'Durum', 'Ödeme', 'İşlemler'].map(h => (
                    <th key={h} className="px-3 py-3 text-left text-xs font-semibold whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(appt => {
                  const itemCount = appt.serviceItems?.length || 1;
                  return (
                    <tr key={appt.id} className={`border-b hover:bg-theme-hover transition-colors ${
                      appt.paymentStatus !== 'Ödendi' ? 'border-l-2' : ''
                    }`}
                      style={{
                        borderColor: 'var(--border-color)',
                        borderLeftColor: appt.paymentStatus !== 'Ödendi' ? '#EF4444' : 'var(--border-color)',
                        background: appt.paymentStatus !== 'Ödendi' ? '#EF444406' : undefined,
                      }}
                    >
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-white flex-shrink-0"
                            style={{ background: 'linear-gradient(135deg, var(--gold), var(--gold-dark))' }}>
                            {appt.customerName.charAt(0)}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate" style={{ color: 'var(--text-primary)' }}>{appt.customerName}</p>
                            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{appt.customerPhone}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="space-y-0.5">
                          {appt.serviceItems && appt.serviceItems.length > 0 ? (
                            appt.serviceItems.map((si, i) => (
                              <p key={i} className="truncate max-w-[180px] text-xs" style={{ color: 'var(--text-primary)' }}>
                                {i > 0 && <span className="mr-1" style={{ color: 'var(--gold)' }}>+</span>}
                                {si.serviceName}
                              </p>
                            ))
                          ) : (
                            <p className="truncate max-w-[180px]" style={{ color: 'var(--text-primary)' }}>{appt.service}</p>
                          )}
                          {itemCount > 1 && (
                            <p className="text-[10px]" style={{ color: 'var(--gold)' }}>{itemCount} hizmet · {appt.duration} dk</p>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="space-y-0.5">
                          {appt.serviceItems && appt.serviceItems.length > 0 ? (
                            appt.serviceItems.map((si, i) => (
                              <p key={i} className="text-xs whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{si.staff}</p>
                            ))
                          ) : (
                            <p className="whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{appt.staff}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <p style={{ color: 'var(--text-primary)' }}>{appt.date}</p>
                        <p className="text-xs" style={{ color: 'var(--gold)' }}>{appt.time}</p>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{appt.source}</td>
                      <td className="px-3 py-3">
                        <StatusBadge status={appt.status}>{appt.status}</StatusBadge>
                      </td>
                      <td className="px-3 py-3">
                        <StatusBadge status={appt.paymentStatus}>{appt.paymentStatus}</StatusBadge>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1">
                          {statusQuickActions.map(action => (
                            <button
                              key={action.status}
                              onClick={() => handleStatusChange(appt.id, action.status)}
                              className="w-7 h-7 rounded-md flex items-center justify-center transition-all cursor-pointer"
                              style={{ background: action.color + '15' }}
                              title={action.label}
                            >
                              <i className={`${action.icon} text-xs`} style={{ color: action.color }} />
                            </button>
                          ))}
                          <button
                            onClick={() => setDetailAppt(appt)}
                            className="w-7 h-7 rounded-md flex items-center justify-center transition-all cursor-pointer"
                            style={{ background: 'var(--bg-tertiary)' }}
                            title="Detay"
                          >
                            <i className="ri-eye-line text-xs" style={{ color: 'var(--text-secondary)' }} />
                          </button>
                          <button
                            onClick={() => openEdit(appt)}
                            className="w-7 h-7 rounded-md flex items-center justify-center transition-all cursor-pointer"
                            style={{ background: 'var(--bg-tertiary)' }}
                            title="Düzenle"
                          >
                            <i className="ri-edit-line text-xs" style={{ color: 'var(--text-secondary)' }} />
                          </button>
                          {appt.paymentStatus !== 'Ödendi' && (
                            <button
                              onClick={() => openPayment(appt)}
                              className="w-7 h-7 rounded-md flex items-center justify-center transition-all cursor-pointer"
                              style={{ background: '#10B98120' }}
                              title="Ödeme Al"
                            >
                              <i className="ri-cash-line text-xs" style={{ color: '#10B981' }} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── CARD VIEW ── */}
      {viewMode === 'card' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map(appt => (
            <div key={appt.id} className={`spa-card p-4 space-y-3 ${
              appt.paymentStatus !== 'Ödendi' ? 'border-l-2' : ''
            }`}
              style={{
                borderLeftColor: appt.paymentStatus !== 'Ödendi' ? '#EF4444' : 'var(--border-color)',
                background: appt.paymentStatus !== 'Ödendi' ? '#EF444406' : undefined,
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold text-white"
                    style={{ background: 'linear-gradient(135deg, var(--gold), var(--gold-dark))' }}>
                    {appt.customerName.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{appt.customerName}</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{appt.customerPhone}</p>
                  </div>
                </div>
                <StatusBadge status={appt.status}>{appt.status}</StatusBadge>
              </div>

              <div className="space-y-1.5">
                {/* Services */}
                <div className="flex items-start gap-2 text-sm">
                  <div className="w-4 h-4 flex items-center justify-center mt-0.5"><i className="ri-drop-line text-xs" style={{ color: 'var(--gold)' }} /></div>
                  <div className="flex-1">
                    {appt.serviceItems && appt.serviceItems.length > 0 ? (
                      <div className="space-y-0.5">
                        {appt.serviceItems.map((si, i) => (
                          <p key={i} className="text-xs" style={{ color: 'var(--text-primary)' }}>
                            {i > 0 && <span style={{ color: 'var(--gold)' }}>+ </span>}
                            {si.serviceName}
                            <span className="text-[10px] ml-1" style={{ color: 'var(--text-muted)' }}>({si.staff})</span>
                          </p>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: 'var(--text-primary)' }}>{appt.service}</span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-time-line text-xs" style={{ color: 'var(--gold)' }} /></div>
                  <span style={{ color: 'var(--text-secondary)' }}>{appt.date} | {appt.time} · {appt.duration} dk</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-line text-xs" style={{ color: 'var(--gold)' }} /></div>
                  <span style={{ color: 'var(--text-secondary)' }}>{appt.source}</span>
                </div>
              </div>

              {appt.notes && (
                <p className="text-xs p-2 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
                  {appt.notes}
                </p>
              )}

              <div className="flex items-center justify-between pt-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
                <div className="flex items-center gap-1.5">
                  <StatusBadge status={appt.paymentStatus}>{appt.paymentStatus}</StatusBadge>
                  <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>₺{appt.price.toLocaleString('tr-TR')}</span>
                </div>
                <div className="flex items-center gap-1">
                  {statusQuickActions.map(action => (
                    <button
                      key={action.status}
                      onClick={() => handleStatusChange(appt.id, action.status)}
                      className="w-7 h-7 rounded-md flex items-center justify-center transition-all cursor-pointer"
                      style={{ background: action.color + '15' }}
                      title={action.label}
                    >
                      <i className={`${action.icon} text-xs`} style={{ color: action.color }} />
                    </button>
                  ))}
                  <button onClick={() => setDetailAppt(appt)} className="w-7 h-7 rounded-md flex items-center justify-center transition-all cursor-pointer" style={{ background: 'var(--bg-tertiary)' }} title="Detay">
                    <i className="ri-eye-line text-xs" style={{ color: 'var(--text-secondary)' }} />
                  </button>
                  <button onClick={() => openEdit(appt)} className="w-7 h-7 rounded-md flex items-center justify-center transition-all cursor-pointer" style={{ background: 'var(--bg-tertiary)' }} title="Düzenle">
                    <i className="ri-edit-line text-xs" style={{ color: 'var(--text-secondary)' }} />
                  </button>
                  {appt.paymentStatus !== 'Ödendi' && (
                    <button
                      onClick={() => openPayment(appt)}
                      className="w-7 h-7 rounded-md flex items-center justify-center transition-all cursor-pointer"
                      style={{ background: '#10B98115' }}
                      title="Ödeme Al"
                    >
                      <i className="ri-cash-line text-xs" style={{ color: '#10B981' }} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {detailAppt && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0" style={{ background: 'var(--modal-overlay)' }} onClick={() => setDetailAppt(null)} />
          <div className="relative bg-theme-card rounded-spa w-full max-w-md flex flex-col max-h-[90vh] animate-scale-in border overflow-hidden" style={{ borderColor: 'var(--card-border)' }}>
            <div className="flex items-center justify-between p-5 flex-shrink-0">
              <h3 className="text-lg font-semibold font-display" style={{ color: 'var(--text-primary)' }}>Randevu Detayı</h3>
              <button onClick={() => setDetailAppt(null)} className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="px-5 pb-5 space-y-3 text-sm overflow-y-auto flex-1 min-h-0">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-semibold text-white" style={{ background: 'linear-gradient(135deg, var(--gold), var(--gold-dark))' }}>
                  {detailAppt.customerName.charAt(0)}
                </div>
                <div>
                  <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>{detailAppt.customerName}</p>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{detailAppt.customerPhone}</p>
                </div>
              </div>

              {/* Multi-service display */}
              <div className="p-3 rounded-spa-sm space-y-2" style={{ background: 'var(--bg-tertiary)' }}>
                <p className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Hizmetler ({detailAppt.serviceItems?.length || 1})</p>
                {detailAppt.serviceItems && detailAppt.serviceItems.length > 0 ? (
                  <div className="space-y-2">
                    {detailAppt.serviceItems.map((si, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <div>
                          <p style={{ color: 'var(--text-primary)' }}>
                            <span style={{ color: 'var(--gold)' }}>{i + 1}. </span>
                            {si.serviceName}
                          </p>
                          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{si.staff} · {si.duration} dk</p>
                        </div>
                        <span className="font-medium" style={{ color: 'var(--gold)' }}>₺{si.price.toLocaleString('tr-TR')}</span>
                      </div>
                    ))}
                    <div className="pt-2 border-t flex items-center justify-between" style={{ borderColor: 'var(--border-color)' }}>
                      <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Toplam</span>
                      <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>₺{detailAppt.price.toLocaleString('tr-TR')}</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between text-sm">
                    <div>
                      <p style={{ color: 'var(--text-primary)' }}>{detailAppt.service}</p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{detailAppt.staff} · {detailAppt.duration} dk</p>
                    </div>
                    <span className="font-medium" style={{ color: 'var(--gold)' }}>₺{detailAppt.price.toLocaleString('tr-TR')}</span>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                  <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Tarih / Saat</p>
                  <p style={{ color: 'var(--text-primary)' }}>{detailAppt.date} | {detailAppt.time}</p>
                </div>
                <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                  <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Kaynak</p>
                  <p style={{ color: 'var(--text-primary)' }}>{detailAppt.source}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge status={detailAppt.status}>{detailAppt.status}</StatusBadge>
                <StatusBadge status={detailAppt.paymentStatus}>{detailAppt.paymentStatus}</StatusBadge>
                <span className="text-sm font-semibold ml-auto" style={{ color: 'var(--text-primary)' }}>₺{detailAppt.price.toLocaleString('tr-TR')}</span>
              </div>
              {detailAppt.notes && (
                <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                  <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Not</p>
                  <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{detailAppt.notes}</p>
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <button onClick={() => { setDetailAppt(null); openEdit(detailAppt); }} className="flex-1 spa-btn-primary text-sm">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-edit-line text-sm" /></div>
                  Düzenle
                </button>
                <button className="flex-1 spa-btn-secondary text-sm flex items-center justify-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-whatsapp-line text-sm" style={{ color: '#10B981' }} /></div>
                  WhatsApp
                </button>
                {detailAppt.paymentStatus !== 'Ödendi' && (
                  <button
                    onClick={() => { setDetailAppt(null); openPayment(detailAppt); }}
                    className="flex-1 text-sm flex items-center justify-center gap-2 text-white rounded-spa-sm px-3 py-2 cursor-pointer"
                    style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}
                  >
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-money-dollar-circle-line text-xs" /></div>
                    Tahsilat Yap
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {modalOpen && (
        <AppointmentModal
          appointment={editAppt}
          initialDate={modalInitialDate}
          initialTime={modalInitialTime}
          initialStaff={modalInitialStaff}
          onClose={() => { setModalOpen(false); setModalInitialDate(undefined); setModalInitialTime(undefined); setModalInitialStaff(undefined); }}
          onSave={handleSave}
        />
      )}

      {/* Quick Payment Modal */}
      {paymentAppt && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
          <div className="absolute inset-0" style={{ background: 'var(--modal-overlay)' }} onClick={() => setPaymentAppt(null)} />
          <div className="relative bg-theme-card rounded-spa w-full max-w-sm flex flex-col animate-scale-in border overflow-hidden" style={{ borderColor: 'var(--card-border)' }}>
            <div className="sticky top-0 flex items-center justify-between p-4 border-b z-10 flex-shrink-0" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
              <h3 className="text-base font-semibold font-display" style={{ color: 'var(--text-primary)' }}>Hızlı Tahsilat</h3>
              <button onClick={() => setPaymentAppt(null)} className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="p-4 space-y-4 overflow-y-auto">
              {/* Müşteri & Hizmet */}
              <div className="p-3 rounded-spa-sm flex items-center gap-3" style={{ background: 'var(--bg-tertiary)' }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold text-white flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, var(--gold), var(--gold-dark))' }}>
                  {paymentAppt.customerName.charAt(0)}
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>{paymentAppt.customerName}</p>
                  <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>{paymentAppt.service}</p>
                  <p className="text-xs font-semibold" style={{ color: 'var(--gold)' }}>₺{paymentAppt.price.toLocaleString('tr-TR')}</p>
                </div>
              </div>

              {/* Tutar */}
              <div>
                <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Kasaya Giren Tutar</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    className="spa-input text-sm flex-1"
                    value={payAmount}
                    onChange={e => setPayAmount(e.target.value)}
                    placeholder="0"
                    min={0}
                  />
                  <select
                    className="spa-input text-sm w-24"
                    value={payCurrency}
                    onChange={e => setPayCurrency(e.target.value as CurrencyCode)}
                  >
                    <option value="TRY">TRY</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
                <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>Fiziksel olarak kasaya/bankaya giren para birimi ve tutar</p>
              </div>

              {/* Hizmet Bedeli */}
              <div>
                <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Müşteriye Kapatılan Hizmet Bedeli</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    className="spa-input text-sm flex-1"
                    value={payServiceAmount}
                    onChange={e => setPayServiceAmount(e.target.value)}
                    placeholder={payAmount || '0'}
                    min={0}
                  />
                  <select
                    className="spa-input text-sm w-24"
                    value={payServiceCurrency}
                    onChange={e => setPayServiceCurrency(e.target.value as CurrencyCode)}
                  >
                    <option value="TRY">TRY</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
                <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>Müşterinin hizmet bedeli olarak kapatılacak tutar (kurla ilgilenmeden)</p>
              </div>

              {/* Ödeme Yöntemi */}
              <div>
                <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Ödeme Yöntemi</label>
                <div className="flex flex-wrap gap-1.5">
                  {payMethods.map(m => (
                    <button
                      key={m}
                      onClick={() => setPayMethod(m)}
                      className="px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-all whitespace-nowrap border"
                      style={{
                        background: payMethod === m ? 'rgba(201,169,98,0.12)' : 'var(--bg-tertiary)',
                        color: payMethod === m ? 'var(--gold)' : 'var(--text-secondary)',
                        borderColor: payMethod === m ? 'var(--gold)' : 'transparent',
                      }}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>

              {/* Banka seçimi (tek ödeme, Havale seçince) */}
              {!paySplitMode && payMethod === 'Havale' && (
                <div>
                  <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Banka Hesabı</label>
                  <select className="spa-input text-sm w-full">
                    {bankListPay.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
              )}

              {/* Parçalı Ödeme Toggle */}
              <div
                className="flex items-center justify-between p-3 rounded-spa border cursor-pointer"
                style={{ background: 'var(--bg-tertiary)', borderColor: 'var(--border-color)' }}
                onClick={() => setPaySplitMode(!paySplitMode)}
              >
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-funds-line text-xs" style={{ color: 'var(--text-secondary)' }} />
                  </div>
                  <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Parçalı Ödeme</span>
                  <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>(Nakit + Banka ayrı kasalara yazılır)</span>
                </div>
                <div className="w-9 h-5 rounded-full relative transition-all" style={{ background: paySplitMode ? '#10B981' : 'var(--border-color)' }}>
                  <div className="w-4 h-4 rounded-full absolute top-0.5 transition-all" style={{ background: '#fff', left: paySplitMode ? '20px' : '2px' }} />
                </div>
              </div>

              {/* Parçalı Ödeme Rows */}
              {paySplitMode && (
                <div className="space-y-2">
                  {paySplits.map((split, idx) => (
                    <div key={idx} className="p-3 rounded-spa border space-y-2" style={{ background: 'var(--bg-tertiary)', borderColor: 'var(--border-color)' }}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Parça {idx + 1} — {split.method === 'Nakit' ? '→ Nakit Kasa' : '→ Banka'}</span>
                        {paySplits.length > 1 && (
                          <button
                            onClick={() => setPaySplits(prev => prev.filter((_, i) => i !== idx))}
                            className="w-5 h-5 flex items-center justify-center cursor-pointer"
                            style={{ color: '#EF4444' }}
                          >
                            <i className="ri-close-line text-xs" />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Tutar</label>
                          <input
                            type="number"
                            className="spa-input text-sm py-1.5"
                            placeholder="0"
                            value={split.amount || ''}
                            onChange={e => setPaySplits(prev => prev.map((s, i) => i === idx ? { ...s, amount: Number(e.target.value) } : s))}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Ödeme Yöntemi</label>
                          <select
                            className="spa-input text-sm py-1.5"
                            value={split.method}
                            onChange={e => setPaySplits(prev => prev.map((s, i) => i === idx ? { ...s, method: e.target.value } : s))}
                          >
                            <option value="Nakit">Nakit</option>
                            <option value="Kredi Kartı">Kredi Kartı</option>
                            <option value="Havale">Havale</option>
                          </select>
                        </div>
                      </div>
                      {split.method === 'Havale' && (
                        <div>
                          <label className="text-[10px] font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Banka</label>
                          <select
                            className="spa-input text-sm py-1.5"
                            value={split.bankName || ''}
                            onChange={e => setPaySplits(prev => prev.map((s, i) => i === idx ? { ...s, bankName: e.target.value } : s))}
                          >
                            <option value="">Banka Seçin</option>
                            {bankListPay.map(b => <option key={b} value={b}>{b}</option>)}
                          </select>
                        </div>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() => setPaySplits(prev => [...prev, { amount: 0, currency: 'TRY', method: 'Nakit', notes: '' }])}
                    className="w-full py-2 rounded-spa text-xs font-medium cursor-pointer border border-dashed flex items-center justify-center gap-1"
                    style={{ borderColor: 'var(--gold)', color: 'var(--gold)' }}
                  >
                    <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-add-line" /></div>
                    Yeni Parça Ekle
                  </button>
                  <div className="flex items-center justify-between text-xs px-1">
                    <span style={{ color: 'var(--text-muted)' }}>Parça Toplamı:</span>
                    <span className="font-bold" style={{ color: 'var(--text-primary)' }}>₺{paySplits.reduce((s, sp) => s + (sp.amount || 0), 0).toLocaleString('tr-TR')}</span>
                  </div>
                </div>
              )}

              {/* Not */}
              <div>
                <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Not</label>
                <textarea
                  className="spa-input text-sm min-h-[70px] resize-none"
                  maxLength={500}
                  value={payNotes}
                  onChange={e => setPayNotes(e.target.value)}
                  placeholder="Opsiyonel not..."
                />
                <p className="text-[10px] mt-1 text-right" style={{ color: 'var(--text-muted)' }}>{payNotes.length}/500</p>
              </div>
            </div>

            <div className="sticky bottom-0 flex items-center justify-end gap-2 p-4 border-t" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
              <button onClick={() => setPaymentAppt(null)} className="spa-btn-secondary text-sm">İptal</button>
              <button
                onClick={handlePaySave}
                className="text-sm text-white rounded-spa-sm px-4 py-2 cursor-pointer flex items-center gap-1.5"
                style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-check-line text-xs" /></div>
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Group Appointment Modal */}
      {groupModalOpen && (
        <GroupAppointmentModal
          onClose={() => setGroupModalOpen(false)}
          onSave={handleGroupSave}
        />
      )}
    </div>
  );
}